import { LiturgyItem } from "../liturgy/types";
import { TenebraeResponse } from "./TenebraePage";

const getPsalm = (ref: string, response: TenebraeResponse): LiturgyItem[] => {
    const ant: LiturgyItem = {
        type: "gabc chant",
        gabc: response.psalms[ref].ant.gabc,
        dropCap: true,
        tone: response.psalms[ref].ant.tone,
        playButtons: [
            {
                gabc: response.psalms[ref].ant.gabc,
                title: "Intone",
                intoneOnly: true,
            },
            {
                gabc: response.psalms[ref].ant.gabc,
                title: "Play full",
                intoneOnly: false,
            },
        ],
        title: "Antiphon",
    };

    return [
        ant,
        {
            type: "tone chant",
            pointedText: response.psalms[ref].psalm,
            tone: response.psalms[ref].tone,
            boldMode: "alternate",
            title: `Psalm ${ref}`,
        },
        ant,
    ];
};

const getRespond = (id, numeral, response: TenebraeResponse): LiturgyItem => {
    return {
        type: "gabc chant",
        gabc:
            response.psalms[`${id}a`].ant.gabc +
            response.psalms[`${id}b`].ant.gabc
                .replace("(c4) ", "")
                .replace("(c3) ", "")
                .replace("(f3) ", ""),
        dropCap: true,
        tone: response.psalms[`${id}a`].ant.tone,
        playButtons: [
            {
                gabc: response.psalms[`${id}a`].ant.gabc,
                title: "Intone",
                intoneOnly: true,
            },
            {
                gabc:
                    response.psalms[`${id}a`].ant.gabc +
                    response.psalms[`${id}b`].ant.gabc
                        .replace("(c4) ", "")
                        .replace("(c3) ", "")
                        .replace("(f3) ", ""),
                title: "Play full",
                intoneOnly: false,
            },
        ],
        title: `${numeral} Respond`,
    };
};

export const getTenebraeLiturgy = (
    day: "thursday" | "friday" | "saturday",
    response: TenebraeResponse
): LiturgyItem[] => {
    if (day === "thursday") {
        return [
            {
                type: "rubric",
                content:
                    "This page provides a number of the simpler proper chants for Tenebrae, but the antiphons and responsories have been set to solemn psalm tones for ease of singing without lengthy choir practice.",
            },
            { type: "spacer" },
            { type: "subheading", content: "I Nocturn" },
            ...getPsalm("69", response),
            ...getPsalm("70", response),
            ...getPsalm("71", response),
            { type: "spacer" },
            { type: "rubric", content: "All stand." },
            {
                type: "gabc chant",
                gabc: "(c4) A(j)rise,(j) O(i) Lord.(h) (::) *And*(f) *main*(f)*tain*(f) *my*(e) *cause.*(d) (::)",
                dropCap: false,
                tone: "1",
                title: "Versicle and Response",
                playButtons: [
                    {
                        gabc: "(c4) A(j)rise,(j) O(i) Lord.(h) (::) *And*(f) *main*(f)*tain*(f) *my*(e) *cause.*(d) (::)",
                        title: "Play",
                        intoneOnly: false,
                    },
                ],
            },
            {
                type: "rubric",
                content:
                    "**Our Father** and **Hail Mary** are said silently, then all sit for the lessons.",
            },
            { type: "spacer" },
            {
                type: "gabc chant",
                gabc: "(c4) A(h)leph.(hvGFggf) (::) How(f) doth(g) the(h) ci(h)ty(h) sit(h) so(h)li(h)ta(h)ry,(h) (,) that(h) was(g) full(ixi) of(h) peo(g)ple:(h) (;) how(h) is(h) she(h) be(h)come(h) as(h) a(h) wi(h)dow,(h) (,) she(h) that(h) was(h) great(h) a(h)mong(h) the(h) na(h)tions,(h) (,) and(h) prin(h)cess(h) a(g)mong(ixi) the(h) pro(g)vin(h)ces,(h) (;) how(h) is(h) she(h) be(h)come(h) tri(g)bu(f)ta(g)ry!(f) (::) Beth.(hvGFggf) (::) She(f) wee(g)peth(h) sore(h) in(h) the(h) night,(h) (,) and(h) her(g) tears(ixi) are(h) on(g) her(h) cheeks:(h) (;) among(h) all(h) her(h) lo(h)vers(h) she(h) hath(h) none(g) to(f) com(g)fort(f) her.(f) (::) All(f) her(g) friends(h) have(h) dealt(g) trea(ixi)che(h)rous(h)ly(g) with(h) her:(h) (;) they(h) are(h) be(h)come(g) her(f) e(g)ne(f)mies.(f) (::) Je(f)ru(g)sa(h)lem,(g) Je(ixi)ru(h)sa(g)lem,(h) (,) re(h)turn(h) to(g) the(f) Lord(g) thy(g) God.(ghGFgvFEdf) (::)",
                dropCap: true,
                tone: "",
                playButtons: [
                    {
                        gabc: "(c4) A(h)leph.(hvGFggf) (::)",
                        title: "Intone",
                        intoneOnly: true,
                    },
                    {
                        gabc: "(c4) (h) to(g) the(f) Lord(g) God.(ghGFgvFEdf) (::)",
                        title: "Ending",
                        intoneOnly: false,
                    },
                ],
                title: "I Lesson",
            },
            getRespond("r1", "I", response),
            {
                type: "gabc chant",
                gabc: "(c4) Ghi(h)mel.(hvGFggf) (::) Ju(f)dah(g) is(h) gone(h) in(h)to(h) cap(h)ti(h)vi(h)ty(h) be(h)cause(h) of(h) af(h)flic(h)tion,(h) (,) and(h) be(h)cause(h) of(g) great(ixi) ser(h)vi(g)tude:(h) (;) she(h) dwel(h)leth(h) a(h)mong(h) the(h) hea(h)then,(h) (,) she(h) fin(g)deth(f) no(g) rest.(f) (::) All(f) her(g) per(h)se(h)cu(h)tors(h) o(h)ver(h)took(h) her(g) be(f)tween(g) the(f) straits.(f) (::) Da(h)leth.(hvGFggf) (::) The(f) ways(g) of(h) Si(h)on(h) do(h) mourn,(h) (,) be(h)cause(h) none(h) come(g) to(ixi) the(h) so(g)lemn(h) feasts:(h) (;) all(h) her(h) gates(h) are(h) de(h)so(h)late;(h) (;) her(h) priests(h) sigh,(h) (,) her(h) vir(h)gins(g) are(ixi) af(h)flic(g)ted,(h) (;) and(h) she(h) is(g) in(f) bit(g)ter(f)ness.(f) (::) He.(hvGFggf) (::) Her(f) ad(g)ver(h)sa(h)ries(h) are(h) the(h) chief,(h) (,) her(g) e(ixi)ne(h)mies(h) pros(g)per;(h) (;) for(h) the(h) Lord(h) hath(h) af(h)flic(h)ted(h) her(h) (,) for(h) the(h) mul(h)ti(h)tude(h) of(h) her(g) trans(f)gres(g)sions.(f) (::) Her(f) chil(g)dren(h) are(h) gone(g) in(ixi)to(h) cap(h)ti(g)vi(h)ty(h) (;) be(h)fore(g) the(f) e(g)ne(f)my.(f) (::) Je(f)ru(g)sa(h)lem,(g) Je(ixi)ru(h)sa(g)lem,(h) (,) re(h)turn(h) to(g) the(f) Lord(g) thy(g) God.(ghGFgvFEdf) (::)",
                dropCap: true,
                tone: "",
                playButtons: [
                    {
                        gabc: "(c4) A(h)leph.(hvGFggf) (::)",
                        title: "Intone",
                        intoneOnly: true,
                    },
                    {
                        gabc: "(c4) (h) to(g) the(f) Lord(g) God.(ghGFgvFEdf) (::)",
                        title: "Ending",
                        intoneOnly: false,
                    },
                ],
                title: "II Lesson",
            },
            getRespond("r2", "II", response),
            {
                type: "gabc chant",
                gabc: "(c4) Vaw.(hvGFggf) (::) From(f) the(g) daugh(h)ter(h) of(h) Zi(h)on(h) all(h) her(h) beau(h)ty(g) is(ixi) de(h)par(g)ted:(h) (;) her(h) prin(h)ces(h) are(h) be(h)come(h) like(h) harts(h) that(g) find(ixi) no(h) pas(g)ture,(h) (;) and(h) they(h) are(h) gone(h) wi(h)thout(h) strength(h) be(h)fore(h) the(g) pur(f)su(g)er.(f) (::) Za(h)yin.(hvGFggf) (::) Je(f)ru(g)sa(h)lem(h) re(h)mem(h)bered(h) in(h) the(h) days(h) of(h) her(h) af(h)flic(h)tion(h) and(g) of(ixi) her(h) mi(g)se(h)ries,(h) (;) all(h) her(h) plea(h)sant(h) things(h) that(h) she(g) had(ixi) in(h) the(h) days(g) of(h) old;(h) (;) when(h) her(h) peo(h)ple(h) fell(h) in(h)to(h) the(h) hand(h) of(h) the(h) e(h)ne(h)my,(h) (,) and(h) none(g) did(f) help(g) her.(f) (::) The(f) ad(g)ver(h)sa(h)ries(h) saw(h) her,(h) (,) and(h) did(h) mock(h) at(g) her(f) sab(g)baths.(f) (::) Heth.(hvGFggf) (::) Je(f)ru(g)sa(h)lem(h) hath(g) grie(ixi)vous(h)ly(g) sinned:(h) (;) there(h)fore(g) she(f) is(g) re(f)moved.(f) (::) All(f) that(g) ho(h)noured(g) her(ixi) des(h)pise(g) her,(h) (;) be(h)cause(h) they(h) have(h) seen(g) her(f) na(g)ked(f)ness:(f) (::) yea,(f) she(g) sigh(h)eth,(h) and(h) tur(g)neth(f) back(g)ward.(f) (::) Teth.(hvGFggf) (::) Her(f) fil(g)thi(h)ness(h) is(h) in(h) her(h) skirts;(h) (,) she(h) re(h)mem(h)ber(h)eth(g) not(ixi) her(h) last(g) end:(h) (;) there(h)fore(h) she(h) came(h) down(h) won(h)der(h)ful(h)ly;(h) (;) she(h) had(g) no(f) com(g)for(f)ter.(f) (::) O(f) Lord,(h) be(g)hold(ixi) my(h) af(h)flic(g)tion:(h) (;) for(h) the(h) e(h)ne(h)my(h) hath(h) mag(g)ni(f)fied(g) him(f)self.(f) (::) Je(f)ru(g)sa(h)lem,(g) Je(ixi)ru(h)sa(g)lem,(h) (,) re(h)turn(h) to(g) the(f) Lord(g) thy(g) God.(ghGFgvFEdf) (::)",
                dropCap: true,
                tone: "",
                playButtons: [
                    {
                        gabc: "(c4) A(h)leph.(hvGFggf) (::)",
                        title: "Intone",
                        intoneOnly: true,
                    },
                    {
                        gabc: "(c4) (h) to(g) the(f) Lord(g) God.(ghGFgvFEdf) (::)",
                        title: "Ending",
                        intoneOnly: false,
                    },
                ],
                title: "III Lesson",
            },
            getRespond("r3", "III", response),
            { type: "spacer" },
            { type: "subheading", content: "II Nocturn" },
            ...getPsalm("72", response),
            ...getPsalm("73", response),
            ...getPsalm("74", response),
            { type: "spacer" },
            { type: "rubric", content: "All stand." },
            {
                type: "gabc chant",
                gabc: "(c4) De(j)li(j)ver(j) my(j) soul(j),(j) O(i) Lord.(h) (::) *From*(f) *the*(f) *hand*(f) *of*(f) *the*(f) *un*(f)*god*(e)*ly.*(d) (::)",
                dropCap: false,
                tone: "1",
                title: "Versicle and Response",
                playButtons: [
                    {
                        gabc: "(c4) De(j)li(j)ver(j) my(j) soul(j),(j) O(i) Lord.(h) (::) *From*(f) *the*(f) *hand*(f) *of*(f) *the*(f) *un*(f)*god*(e)*ly.*(d) (::)",
                        title: "Play",
                        intoneOnly: false,
                    },
                ],
            },
            {
                type: "rubric",
                content:
                    "**Our Father** and **Hail Mary** are said silently, then all sit for the lessons.",
            },
            { type: "spacer" },
            { type: "subheading", content: "IV Lesson" },
            {
                type: "rubric",
                content: "Exposition of Saint Augustine on Psalm 64",
            },
            {
                type: "text",
                content:
                    "*Hear my voice, O God, in my prayer: preserve my life from fear of the enemy.* Ye know, holy and beloved brethren, that your Lord Jesus Christ is your Head, and that all they which cleave unto him are members of that Head. And it is his voice which ye hear, and that most plainly, inasmuch as it proceedeth not from the Head only, but from the whole Body. Wherefore let us also say: Hear my voice, O God, in my prayer: preserve my life from fear of the enemy. The enemy raged furiously against the martyrs. What said that voice of the Body of Christ? It pleaded that they might be delivered from their enemies; that the enemy might have no power to slay them.",
            },
            getRespond("r4", "IV", response),
            { type: "spacer" },
            { type: "subheading", content: "V Lesson" },
            {
                type: "rubric",
                content: "Exposition of Saint Augustine on Psalm 64",
            },
            {
                type: "text",
                content:
                    "Suppose ye that because they were slain, therefore they were not heard? Hath the Lord ever forsaken his servants that be of a contrite heart? Or hath he ever despised them that put their trust in him? God forbid! Yea, they were heard, and yet they were slain: notwithstanding they were delivered from their enemies. This, then, is the martyr’s prayer: *Preserve my life from the fear of the enemy;* not that the enemy should have no power to slay me, but that I should not fear the enemy that slayeth. Preserve me from fear of the enemy, and keep my in thy fear. Let me not fear him that killeth the body, but rather let me fear him which is able to destroy both body and soul in hell; yea, I say, let me fear him. For I desire not that I may be without fear, but that, being preserved from fear of the enemy, I may, with fear, become the servant of God.",
            },
            getRespond("r5", "V", response),
            { type: "spacer" },
            { type: "subheading", content: "VI Lesson" },
            {
                type: "rubric",
                content: "Exposition of Saint Augustine on Psalm 64",
            },
            {
                type: "text",
                content:
                    "*Thou hast hidden me, O God, from the gathering together of the froward, and from the insurrection of wicked doers.* Now let us contemplate our Head himself. Many martyrs have suffered such things, but none shineth with such glory as the Head of the martyrs: in him we best perceive what they endured. He was hidden from the insurrection of the froward; for God hid himself. He, the Very Son made man, hid his own Flesh; for he is Son of man and Son of God; Son of God, being in the form of God; Son of man, being in the form of a servant, having power to lay down his life, and having power to take it again.",
            },
            getRespond("r6", "VI", response),
            { type: "spacer" },
        ];
    }

    throw new Error("");
};
